<template>
	<div :class="['eventNote py-03 pl-04 pr-13', { 'pl-04 pr-13': editable, 'px-04 pl-04': !editable }]">
		<div class="text font-paragraph-s text-ink-87 [overflow-wrap:anywhere]">{{ note.text }}</div>

		<div class="details font-caption flex items-center gap-03 text-ink-38">
			{{ getFormattedNoteTimestamp(note) }}
		</div>

		<div v-if="editable" class="actions absolute right-03 top-03 mt-01 flex items-center">
			<Tooltip :content="t('eventNotes.editTooltip')">
				<Telemetry name="Edit event note">
					<IconButton icon="pencil-outline" size="s" @click="emit('edit', note)" />
				</Telemetry>
			</Tooltip>

			<Tooltip :content="t('eventNotes.removeTooltip')">
				<Telemetry name="Delete event note">
					<IconButton icon="delete-outline" size="s" @click="emit('remove', note)" />
				</Telemetry>
			</Tooltip>
		</div>
	</div>
</template>

<script setup lang="ts">
import { IconButton, Tooltip, useEnvironment, useFormatter, useTranslation } from '@bluebrick/saas-ui'
import { type DateTime } from 'luxon'
import Telemetry from '@/common/components/Telemetry.vue'
import { type Note } from '@/types'

const { t } = useTranslation()
const { dateDayMonthYear, time } = useFormatter()
const { setSystemLocale } = useEnvironment()

defineProps<{
	note: Note
	editable?: boolean
}>()

const emit = defineEmits<{
	edit: [note: Note]
	remove: [note: Note]
}>()

const dayMonthFormatter = new Intl.DateTimeFormat(navigator.language, {
	month: 'short',
	day: 'numeric'
})

function getFormattedDateTime(dateTime?: DateTime) {
	if (!dateTime || !dateTime.isValid) {
		return ''
	}

	const asJSDate = dateTime.toJSDate()

	setSystemLocale(navigator.language)

	// If note was added more then a year ago then add year to the timestamp
	// Otherwise just use day and month
	if (Math.abs(dateTime.diffNow().as('years')) > 1) {
		return `${dateDayMonthYear(asJSDate)} ${time(asJSDate)}`
	}

	return `${dayMonthFormatter.format(asJSDate)} ${time(asJSDate)}`
}

function getFormattedNoteTimestamp(note: Note) {
	const formattedCreateDateTime = getFormattedDateTime(note.create_time)
	const formattedUpdateDateTime = getFormattedDateTime(note.update_time)

	if (!formattedCreateDateTime) {
		return ''
	}

	if (formattedUpdateDateTime) {
		return `${formattedCreateDateTime} (${t('eventNotes.updated')} ${formattedUpdateDateTime})`
	}

	return formattedCreateDateTime
}
</script>

<style lang="scss" scoped>
.eventNote {
	background-color: rgb(from theme('colors.message.info') r g b / 0.12);

	.actions {
		display: none;

		:deep(.icon-button-icon) {
			color: theme('colors.primary.60');

			&:hover {
				color: theme('colors.primary.100');
			}
		}
	}

	&:hover {
		position: relative;

		.actions {
			display: flex;
		}
	}
}
</style>
